import React from "react";
import { Scissors, Calculator, Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { formatValue } from "@/lib/api";
import { SENTIMENT_COLOR, TRADES_COLOR } from "@/components/SignalTags";

const ICONS = { scissors: Scissors, calculator: Calculator, sparkles: Sparkles };
// Uniform market-signal styling (red scissors = sentiment, gray calculator = trades).
const VECTOR_STYLE = {
  sentiment: { color: SENTIMENT_COLOR, glow: false },
  trades: { color: TRADES_COLOR, glow: false },
  consensus: { color: "var(--accent)", glow: true },
};

// Small inline badge reused on the Trade Calculator rows.
export const DivergenceChip = ({ divergence }) => {
  if (!divergence?.flagged) return null;
  const sell = divergence.kind === "sell_high";
  const Icon = sell ? TrendingUp : TrendingDown;
  const color = sell ? "#F59E0B" : "var(--accent)";
  return (
    <span
      title={sell ? "Hype Premium — crowd over-rates vs real trades" : "Trade Discount — real trades outrun the crowd"}
      data-testid="divergence-chip"
      className="inline-flex items-center gap-0.5 font-mono text-[9px] font-semibold px-1 py-0.5 rounded-sm border align-middle"
      style={{ color, borderColor: `${color}55`, background: `${color}14` }}
    >
      <Icon className="w-2.5 h-2.5" />{sell ? "HYPE" : "VALUE"}
    </span>
  );
};

export default function MarketVectors({ vectors = [], divergence, format }) {
  const hasSplit = vectors.some(v => v.key === "sentiment") && vectors.some(v => v.key === "trades");
  if (!hasSplit) return null;
  const sell = divergence?.kind === "sell_high";
  const gapAbs = divergence ? Math.abs(divergence.gap) : 0;
  const FMT_LABEL = { "1qb": "1QB", superflex: "Superflex", tep: "TE Premium" };
  const fmtLabel = FMT_LABEL[format] || "1QB";

  return (
    <section data-testid="market-vectors" className="space-y-3">
      <div className="text-xs tracking-widest text-[var(--text-3)] flex items-center gap-2">
        VALUATION VECTOR · MARKET SIGNAL GRID <span className="text-[var(--accent)] normal-case tracking-normal font-mono text-[10px]" data-testid="vectors-format-label">({fmtLabel} basis)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {vectors.map(v => {
          const st = VECTOR_STYLE[v.key] || VECTOR_STYLE.consensus;
          const Icon = ICONS[v.icon] || Sparkles;
          return (
            <div
              key={v.key}
              data-testid={`vector-${v.key}`}
              className={`card-bordered p-4 transition-colors ${st.glow ? "neon-border bg-[var(--accent)]/5" : ""}`}
              style={st.glow ? {} : { borderColor: `${st.color}33` }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-sm border"
                  style={{ color: st.color, borderColor: `${st.color}55`, background: `${st.color}14` }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="text-[10px] tracking-widest uppercase leading-tight" style={{ color: st.glow ? "var(--accent)" : "var(--text-2)" }}>
                  {v.label}
                </div>
              </div>
              <div className="font-mono text-2xl mt-3" data-testid={`vector-value-${v.key}`} style={st.glow ? { color: "var(--accent)" } : {}}>
                {formatValue(v.value)}
              </div>
              <div className="text-[11px] text-[var(--text-3)] mt-2 leading-snug">{v.characteristic}</div>
            </div>
          );
        })}
      </div>

      {divergence?.flagged && (
        <div
          data-testid="divergence-banner"
          className="card-bordered p-4 flex items-start gap-3"
          style={{ borderColor: sell ? "#F59E0B66" : "rgba(204,255,0,0.4)", background: sell ? "#F59E0B0F" : "rgba(204,255,0,0.06)" }}
        >
          {sell ? <TrendingUp className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#F59E0B" }} />
                : <TrendingDown className="w-5 h-5 mt-0.5 shrink-0 text-[var(--accent)]" />}
          <div>
            <div className="text-xs tracking-widest font-mono mb-1" style={{ color: sell ? "#F59E0B" : "var(--accent)" }}>
              {sell ? "⚠ HYPE PREMIUM DETECTED" : "BUY-LOW WINDOW"}
            </div>
            <p className="text-sm text-[var(--text-2)]">
              {sell
                ? `Public sentiment ranks this asset ~${gapAbs} percentile points HIGHER than where it actually trades. The crowd is over-paying — sell into the hype before the market corrects.`
                : `Real, confirmed trades value this asset ~${gapAbs} percentile points HIGHER than the crowd does. Buy it cheap from sentiment-anchored managers before they catch up.`}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
